import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import {
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  ModalFooter,
  FormFeedback,
  Badge,
  UncontrolledTooltip,
  CustomInput,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  FaUsersCog,
  FaPlus,
  FaPowerOff,
  FaRandom,
  FaBriefcase,
  FaSitemap,
  FaCog,
  FaIdCard,
  FaEnvelope,
  FaUser,
  FaPhone,
  FaShieldAlt,
  FaUserCircle,
  FaCreditCard,
  FaPlug,
  FaFileInvoiceDollar,
  FaExchangeAlt,
  FaMoneyCheckAlt,
  FaLink,
  FaCalculator,
} from 'react-icons/fa';
import { FormattedMessage, useIntl } from 'react-intl';
import PlanPermissionCheck from '../../../utils/PlanPermissionCheck';

import { history } from '../../../history';
import { formatMobilePhone, getOnlyNumbers } from '../../../utils/formaters';

import PermissionGate from '../../../PermissionGate';
import API from '../../../services/API.redux';
import { applicationActions } from '../../../new.redux/actions';
import IuliPlanInfo from '../../../views/admin/company/edit/IuliPlanInfo';
import { getAuthenticatedUser } from '../../../services/apis/auth.api';
import LanguageDropdownMenu from '../../../components/language-selector/LanguageDropdownMenu';
import CurrencyDropdownMenu from '../../../components/currency-selector/CurrencyDropdownMenu';

import { store } from '../../../redux/storeConfig/store';
import {
  getShowProductNfCopySection,
  setShowProductNfCopySection,
} from '../../../utils/productFormPreferences';

const normalizeUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  try {
    const [base, query] = url.split('?');
    const parts = base.split('://');
    if (parts.length < 2) return base.replace(/\/{2,}/g, '/') + (query ? `?${query}` : '');
    const [protocol, rest] = parts;
    const normalizedBase = `${protocol}://${rest.replace(/\/{2,}/g, '/')}`;
    return query ? `${normalizedBase}?${query}` : normalizedBase;
  } catch (_) {
    return url;
  }
};

const buildAvatarSrc = (url, bust) => {
  if (!url) return url;
  const normalized = normalizeUrl(url);
  const isSigned = /X-Amz-Algorithm=/.test(normalized);
  if (isSigned) return normalized;
  if (bust) {
    return normalized.includes('?') ? `${normalized}&t=${bust}` : `${normalized}?t=${bust}`;
  }
  return normalized;
};

const NavbarUser = ({ userData, userImg, logout, currentCompany, isIuliAdmin, currentPermissions, userPermissions, dispatch }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isIuliModalOpen, setIsIuliModalOpen] = useState(false);
  const [showProductNfCopyPref, setShowProductNfCopyPref] = useState(true);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarBust, setAvatarBust] = useState(0);
  const fileInputRef = useRef(null);
  const avatarTriggerId = `avatar-upload-${userData?.id || 'me'}`;

  useEffect(() => {
  }, [userData]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.avatar]);

  useEffect(() => {
    if (isProfileModalOpen) {
      setShowProductNfCopyPref(getShowProductNfCopySection());
    }
  }, [isProfileModalOpen]);

  const toggle = () => setIsOpen(!isOpen);
  const toggleSecurityModal = () => setIsSecurityModalOpen(!isSecurityModalOpen);
  const toggleProfileModal = () => setIsProfileModalOpen(!isProfileModalOpen);
  const toggleIuliModal = () => setIsIuliModalOpen(!isIuliModalOpen);

  const handleNavigation = (e, path, id = null) => {
    e.preventDefault();

    if (id && !isIuliAdmin && currentPermissions) {
      const hasPermission = currentPermissions.some(
        (permission) => permission === id
      );

      if (!hasPermission) {
        store.dispatch(
          applicationActions.toggleDialog({
            type: 'warning',
            title: 'Aviso',
            message: 'Seu Plano não Possui acesso a esta Funcionalidade.',
            hasTimeout: true,
          })
        );

        return;
      }
    }

    history.push(path);
  };

  const securityFormik = useFormik({
    initialValues: {
      email: userData.email || '',
      phone: userData.mobile_phone || '',
      mobile_phone_ddi: userData.ddi || '55',
      old_password: '',
      password: '',
      password_confirmation: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Email inválido').required('Campo obrigatório'),
      phone: Yup.string()
        .required('Campo obrigatório')
        .test(
          'phone',
          'Telefone inválido',
          (value) => getOnlyNumbers(value).length >= 10
        ),
      mobile_phone_ddi: Yup.string().required('Campo obrigatório'),
      old_password: Yup.string().required('Campo obrigatório'),
      password: Yup.string()
        .min(8, 'A senha deve ter pelo menos 8 caracteres')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
          'A senha deve conter pelo menos uma letra maiúscula, uma minúscula, um número e um caractere especial'
        )
        .required('Campo obrigatório'),
      password_confirmation: Yup.string()
        .oneOf([Yup.ref('password'), null], 'As senhas devem corresponder')
        .required('Campo obrigatório'),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await updateUser({
          id: userData.id,
          mobile_phone_ddi: values.mobile_phone_ddi,
          email: values.email,
          mobile_phone: values.phone,
          old_password: values.old_password,
          password: values.password,
          apiOptions: { params: { 'change-password': true } },
        });

        if (response.status === 204) {
          dispatch(
            applicationActions.toggleDialog({
              type: 'success',
              title: 'Sucesso',
              message: 'Dados atualizados com sucesso!',
              hasTimeout: true,
            })
          );
          resetForm();
          toggleSecurityModal();
        }
      } catch (error) {
        console.error('Erro na requisição:', error);
      }
    },
  });

  const profileFormik = useFormik({
    initialValues: {
      email: userData.email || '',
      phone: userData.mobile_phone || '',
      mobile_phone_ddi: userData.ddi || '55',
      old_password: '',
      avatar: null,
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Email inválido').required('Campo obrigatório'),
      phone: Yup.string()
        .required('Campo obrigatório')
        .test('phone', 'Telefone inválido', (value) => getOnlyNumbers(value).length >= 10),
      mobile_phone_ddi: Yup.string().required('Campo obrigatório'),
      old_password: Yup.string().required('Campo obrigatório'),
    }),
  onSubmit: async (values) => {
      try {
        const onlyNumbersPhone = getOnlyNumbers(values.phone);
        const updateRes = await updateUser({
          id: userData.id,
          mobile_phone_ddi: values.mobile_phone_ddi,
          email: values.email,
          mobile_phone: onlyNumbersPhone,
          old_password: values.old_password,
          avatar: values.avatar,
          apiOptions: { params: { 'change-profile': true } },
        });

  const updatedAvatarFromRes = updateRes?.data?.avatar || updateRes?.user?.avatar || updateRes?.avatar || null;
        if (updatedAvatarFromRes) {
          dispatch({
            type: 'UPDATE_LOGGED_USER',
            payload: { avatar: updatedAvatarFromRes },
          });
          setAvatarBust((n) => n + 1);
        }

        let fetched = null;
        try {
          const authUser = await getAuthenticatedUser();
          fetched = authUser?.data || authUser?.user || authUser || null;
        } catch (_) {}

    dispatch({
          type: 'UPDATE_LOGGED_USER',
          payload: {
      email: fetched?.email ?? userData.email,
      mobile_phone: fetched?.mobile_phone ?? userData.mobile_phone,
      ddi: fetched?.mobile_phone_ddi ?? userData.ddi,
      avatar: fetched?.avatar ?? updatedAvatarFromRes ?? userData.avatar,
          },
        });
    if ((fetched?.avatar ?? updatedAvatarFromRes)) setAvatarBust((n) => n + 1);
        setAvatarPreview(null);
        toggleProfileModal();
        dispatch(applicationActions.toggleDialog({
          type: 'success',
          title: 'Sucesso',
          message: 'Dados do perfil atualizados com sucesso!',
          hasTimeout: true,
        }));
      } catch (error) {
        console.error('Erro na requisição:', error);
      }
    },
  });

  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  return (
    <ul className="nav navbar-nav navbar-nav-user float-right">
      <UncontrolledDropdown tag="li" className="dropdown-user nav-item" isOpen={isOpen} toggle={toggle}>
        <DropdownToggle tag="a" className="nav-link dropdown-user-link d-flex align-items-center">
          <div style={{ cursor: 'pointer', marginTop: -10, marginRight: -15 }}>
            <FaCog className='dropdown-user nav-item mt-2' style={{ fontSize: 20 }} />
          </div>
        </DropdownToggle>
        <DropdownMenu right>
          {currentCompany && (
            <>
              <DropdownItem tag="a" href="#" onClick={(e) => { e.preventDefault(); toggle(); toggleIuliModal(); }}>
                <FaCreditCard size={14} className="mr-50" />
                <span className="align-middle">
                  <FormattedMessage id="settings.menu.subscription" />
                </span>
              </DropdownItem>
              <PermissionGate permissions="companies.show">
                <DropdownItem
                  tag="a"
                  href="#"
                  onClick={(e) => {
                    handleNavigation(
                      e,
                      `/admin/company/company-data/${currentCompany.id}`
                    );
                    toggle();
                  }}
                >
                  <FaBriefcase size={14} className="mr-50" />
                  <span className="align-middle">
                    <FormattedMessage id="settings.menu.company_data" />
                  </span>
                </DropdownItem>
              </PermissionGate>
              <PermissionGate permissions="companies.show">
                <DropdownItem
                  tag="a"
                  href="#"
                  onClick={(e) => {
                    handleNavigation(
                      e,
                      `/admin/company/platform-integrations/${currentCompany.id}`,
                      'platform-integrations'
                    );
                    toggle();
                  }}
                >
                  <FaPlug size={14} className="mr-50" />
                  <span className="align-middle">
                    <FormattedMessage id="settings.menu.platform_integration" />
                  </span>
                </DropdownItem>
              </PermissionGate>
              <PermissionGate permissions="companies.show">
                <DropdownItem
                  tag="a"
                  href="#"
                  onClick={(e) => {
                    handleNavigation(
                      e,
                      `/admin/company/invoice-settings/${currentCompany.id}`,
                      'invoice-settings'
                    );
                    toggle();
                  }}
                >
                  <FaFileInvoiceDollar size={14} className="mr-50" />
                  <span className="align-middle">
                    <FormattedMessage id="settings.menu.invoice_settings" />
                  </span>
                </DropdownItem>
              </PermissionGate>
              <PermissionGate permissions="companies.show">
                <DropdownItem
                  tag="a"
                  href="#"
                  onClick={(e) => {
                    handleNavigation(
                      e,
                      `/admin/company/received-invoice-settings/${currentCompany.id}`,
                      'received-invoice-settings'
                    );
                    toggle();
                  }}
                >
                  <FaFileInvoiceDollar size={14} className="mr-50" />
                  <span className="align-middle">
                    <FormattedMessage id="settings.menu.received_invoice_settings" />
                  </span>
                </DropdownItem>
              </PermissionGate>
              <PermissionGate permissions="companies.accounting.manage">
                <DropdownItem
                  tag="a"
                  href="#"
                  onClick={(e) => {
                    handleNavigation(
                      e,
                      `/admin/company/accounting-integration/${currentCompany.id}`,
                      'accounting-integration'
                    );
                    toggle();
                  }}
                >
                  <FaCalculator size={14} className="mr-50" />
                  <span className="align-middle">
                    <FormattedMessage id="settings.menu.accounting_integration" />
                  </span>
                </DropdownItem>
              </PermissionGate>
              {/* Billets & Scheduling: visible only for iuli_admin */}
              {(isIuliAdmin || PlanPermissionCheck('billets-resource', intl, false)) && (
                <DropdownItem
                  tag="a"
                  href="#"
                  onClick={(e) => {
                    handleNavigation(
                      e,
                      `/admin/company/billets-scheduling/${currentCompany.id}`,
                      null
                    );
                    toggle();
                  }}
                >
                  <FaMoneyCheckAlt size={14} className="mr-50" />
                  <span className="align-middle">
                    <FormattedMessage id="settings.menu.billets_scheduling" />
                  </span>
                </DropdownItem>
              )}
              <DropdownItem
                tag="a"
                href="#"
                onClick={(e) => {
                  handleNavigation(
                    e,
                    `/admin/company/open-finance/${currentCompany.id}`,
                    null
                  );
                  toggle();
                }}
              >
                <FaLink size={14} className="mr-50" />
                <span className="align-middle">
                  <FormattedMessage id="settings.menu.open_finance" />
                </span>
              </DropdownItem>
              {(isIuliAdmin || (currentPermissions?.includes('sales-api') && userPermissions?.some(({ name }) => name === 'sales-api.access'))) && (
                <DropdownItem
                  tag="a"
                  href="#"
                  onClick={(e) => {
                    handleNavigation(
                      e,
                      `/admin/company/sales-api/${currentCompany.id}`,
                      null
                    );
                    toggle();
                  }}
                >
                  <FaExchangeAlt size={14} className="mr-50" />
                  <span className="align-middle">
                    <FormattedMessage id="settings.menu.sales_api" />
                  </span>
                </DropdownItem>
              )}
              <DropdownItem divider />
              <PermissionGate permissions="permissions-users-companies.list">
                <DropdownItem
                  tag="a"
                  href="#"
                  onClick={(e) => {
                    handleNavigation(e, '/admin/company-user/list');
                    toggle();
                  }}
                >
                  <FaUsersCog size={14} className="mr-50" />
                  <span className="align-middle">
                    <FormattedMessage id="settings.menu.manage_users" />
                  </span>
                </DropdownItem>
              </PermissionGate>
              <PermissionGate permissions="api.companies.bank_accounts.import-ofx">
                <DropdownItem
                  tag="a"
                  href="#"
                  onClick={(e) => handleNavigation(
                    e,
                    '/admin/reconciliation-rule/list',
                    'reconciliation_rules'
                  )}
                >
                  <FaRandom size={14} className="mr-50" />
                  <span className="align-middle">
                    <FormattedMessage id="settings.menu.reconciliation_rules" />
                  </span>
                </DropdownItem>
              </PermissionGate>
            </>
          )}
          <DropdownItem
            tag="a"
            href="#"
            onClick={(e) => {
              handleNavigation(e, '/admin/company/edit');
              toggle();
            }}
          >
            <FaPlus size={14} className="mr-50" />
            <span className="align-middle">
              <FormattedMessage id="button.create.company" />
            </span>
          </DropdownItem>
          </DropdownMenu>
      </UncontrolledDropdown>
      <LanguageDropdownMenu />
      <UncontrolledDropdown tag="li" className="dropdown-user nav-item">
        <DropdownToggle tag="a" className="nav-link dropdown-user-link d-flex align-items-center">
          <div className="d-flex flex-column text-right mr-50">
            <span className="font-weight-bold">{userData.name}</span>
            <span className="small font-weight-normal text-muted mt-1">
              <FormattedMessage id="user.menu.available" />
            </span>
          </div>
          <span data-tour="user">
            <img
              src={userData.avatar && !String(userData.avatar).startsWith('blob:') ? buildAvatarSrc(userData.avatar, avatarBust) : userImg}
              className="round"
              height="40"
              width="40"
              alt="avatar"
              style={{ objectFit: 'cover' }}
            />
          </span>
        </DropdownToggle>
        <DropdownMenu right>
          <DropdownItem
            tag="span"
          >
            <FaIdCard size={14} className="mr-50" />
            <span>
              <FormattedMessage id="user.menu.my_id" />: &nbsp;
              <b className="align-middle">{userData.id}</b>
            </span>

          </DropdownItem>
          <DropdownItem
            tag="span"
          >
            <FaUserCircle size={14} className="mr-50" />
            <span>
              <FormattedMessage id="user.menu.name" />: &nbsp;
              <b className="align-middle">{userData.name}</b>
            </span>

          </DropdownItem>

          <DropdownItem
            tag="span"
          >
            <FaPhone size={14} className="mr-50" />
            <span>
              <FormattedMessage id="user.menu.phone" />: &nbsp;
              <b className="align-middle">{userData.mobile_phone}</b>
            </span>

          </DropdownItem>

          <DropdownItem
            tag="span"
          >
            <FaEnvelope size={14} className="mr-50" />
            <span>
              <FormattedMessage id="user.menu.email" />: &nbsp;
              <b className="align-middle">{userData.email}</b>
            </span>

          </DropdownItem>

          <CurrencyDropdownMenu userData={userData} onCurrencyChange={async (currency) => {
            try {
              const authUser = await getAuthenticatedUser();
              const fetched = authUser?.data || authUser?.user || authUser || null;
              if (fetched) {
                const updatedCurrency = currency !== undefined ? currency : (fetched?.currency || null);

                dispatch({
                  type: 'UPDATE_LOGGED_USER',
                  payload: {
                    email: fetched?.email ?? userData.email,
                    mobile_phone: fetched?.mobile_phone ?? userData.mobile_phone,
                    ddi: fetched?.mobile_phone_ddi ?? userData.ddi,
                    avatar: fetched?.avatar ?? userData.avatar,
                    currency: updatedCurrency,
                  },
                });

                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('currencyFormatUpdate'));
                }
              }
            } catch (error) {
              console.error('Erro ao recarregar usuário:', error);
            }
          }} />

          <DropdownItem divider />
          <DropdownItem tag="a" onClick={() => toggleProfileModal()} style={{ cursor: 'pointer' }}>
            <FaUser size={14} className="mr-50" />
            <span className="align-middle">
              <FormattedMessage id="user.menu.my_profile" />
            </span>
          </DropdownItem>
          <DropdownItem tag="a" onClick={() => toggleSecurityModal()} style={{ cursor: 'pointer' }}>
            <FaShieldAlt size={14} className="mr-50" />
            <span className="align-middle">
              <FormattedMessage id="user.menu.security" />
            </span>
          </DropdownItem>
          <DropdownItem
            tag="a"
            href="/pages/login"
            onClick={(e) => {
              e.preventDefault();
              return logout();
            }}
          >
            <FaPowerOff size={14} className="mr-50" />
            <span className="align-middle">
              <FormattedMessage id="user.menu.logout" />
            </span>
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
      <Modal
        isOpen={isProfileModalOpen}
        toggle={toggleProfileModal}
        onClosed={() => {
          profileFormik.setFieldValue('old_password', '');
          profileFormik.setFieldTouched('old_password', false);
          profileFormik.setFieldValue('avatar', null);
          if (avatarPreview) {
            URL.revokeObjectURL(avatarPreview);
            setAvatarPreview(null);
          }
        }}
        className="modal-dialog-centered"
      >
        <ModalHeader toggle={toggleProfileModal}>
          <FormattedMessage id="user.profile.title" />
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={profileFormik.handleSubmit}>
            <FormGroup>
              <Label for="avatar">
                <FormattedMessage id="user.profile.avatar" />
              </Label>
              <Input
                innerRef={fileInputRef}
                type="file"
                name="avatar"
                id="avatar"
                accept="image/*"
                onChange={(e) => {
                  const file = e.currentTarget.files && e.currentTarget.files[0];
                  if (file) {
                    profileFormik.setFieldValue('avatar', file);
                    const previewUrl = URL.createObjectURL(file);
                    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
                    setAvatarPreview(previewUrl);
                  } else {
                    profileFormik.setFieldValue('avatar', null);
                    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
                    setAvatarPreview(null);
                  }
                }}
                style={{ display: 'none' }}
              />

              <div className="d-flex flex-column align-items-center justify-content-center mt-1 text-center">
                <div
                  id={avatarTriggerId}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  style={{ cursor: 'pointer' }}
                  title=""
                >
                  <img
                    src={avatarPreview || (userData.avatar && !String(userData.avatar).startsWith('blob:') ? buildAvatarSrc(userData.avatar, avatarBust) : userImg)}
                    alt="avatar"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '50%', border: '1px solid #ddd' }}
                  />
                </div>
                <UncontrolledTooltip placement="top" target={avatarTriggerId}>
                  <FormattedMessage id="user.profile.attach_photo" />
                </UncontrolledTooltip>
                <small className="form-text text-muted mt-1">
                  <FormattedMessage id="user.profile.avatar_click" />
                </small>
                {avatarPreview && (
                  <Button
                    color="link"
                    className="p-0 mt-1"
                    onClick={() => {
                      profileFormik.setFieldValue('avatar', null);
                      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
                      setAvatarPreview(null);
                    }}
                  >
                    <FormattedMessage id="user.profile.remove" />
                  </Button>
                )}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="email">
                <FormattedMessage id="user.profile.email" />
              </Label>
              <Input
                type="email"
                name="email"
                id="email"
                placeholder={intl.formatMessage({ id: 'user.profile.email_placeholder' })}
                value={profileFormik.values.email}
                onChange={profileFormik.handleChange}
                onBlur={profileFormik.handleBlur}
                invalid={profileFormik.touched.email && profileFormik.errors.email}
              />
              <FormFeedback>{profileFormik.errors.email}</FormFeedback>
            </FormGroup>
            <FormGroup>
              <Label for="phone">
                <FormattedMessage id="user.profile.phone" />
              </Label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Input
                  type="text"
                  name="mobile_phone_ddi"
                  id="mobile_phone_ddi"
                  placeholder="55"
                  style={{ width: '60px', marginRight: '10px', padding: '0.2rem 0.5rem' }}
                  value={profileFormik.values.mobile_phone_ddi}
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  invalid={profileFormik.touched.mobile_phone_ddi && profileFormik.errors.mobile_phone_ddi}
                />
                <Input
                  type="text"
                  name="phone"
                  id="phone"
                  placeholder="(XX) XXXXX-XXXX"
                  value={formatMobilePhone(profileFormik.values.phone)}
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  invalid={profileFormik.touched.phone && profileFormik.errors.phone}
                />
              </div>
              {profileFormik.touched.mobile_phone_ddi && profileFormik.errors.mobile_phone_ddi ? (
                <FormFeedback style={{ display: 'block' }}>{profileFormik.errors.mobile_phone_ddi}</FormFeedback>
              ) : null}
              <FormFeedback>{profileFormik.errors.phone}</FormFeedback>
            </FormGroup>
            <FormGroup>
              <Label for="old_password">
                <FormattedMessage id="user.profile.my_password" />
              </Label>
              <Input
                type="password"
                name="old_password"
                id="old_password"
                placeholder={intl.formatMessage({ id: 'user.profile.my_password_placeholder' })}
                value={profileFormik.values.old_password}
                onChange={profileFormik.handleChange}
                onBlur={profileFormik.handleBlur}
                invalid={profileFormik.touched.old_password && profileFormik.errors.old_password}
              />
              <FormFeedback>{profileFormik.errors.old_password}</FormFeedback>
            </FormGroup>
            <hr className="my-2" />
            <FormGroup className="mb-2">
              <Label className="text-uppercase text-muted small d-block mb-1">
                <FormattedMessage id="user.preferences.section_title" />
              </Label>
              <CustomInput
                type="switch"
                id="pref_show_product_nf_copy"
                name="pref_show_product_nf_copy"
                checked={showProductNfCopyPref}
                onChange={() => {
                  const next = !showProductNfCopyPref;
                  setShowProductNfCopyPref(next);
                  setShowProductNfCopySection(next);
                }}
              >
                <span className="switch-label">
                  <FormattedMessage id="user.preferences.show_product_nf_copy" />
                </span>
              </CustomInput>
              <small className="form-text text-muted mt-50 d-block">
                <FormattedMessage id="user.preferences.show_product_nf_copy_hint" />
              </small>
            </FormGroup>
            <ModalFooter>
              <Button color="secondary" onClick={toggleProfileModal}>
                <FormattedMessage id="button.cancel" />
              </Button>{' '}
              <Button color="primary" type="submit" onClick={profileFormik.handleSubmit}>
                <FormattedMessage id="button.save" />
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={isSecurityModalOpen}
        toggle={toggleSecurityModal}
        onClosed={() => securityFormik.resetForm()}
        className="modal-dialog-centered"
      >
        <ModalHeader toggle={toggleSecurityModal}>
          <FormattedMessage id="user.security.title" />
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={securityFormik.handleSubmit}>
            <FormGroup>
              <Label for="old_password">
                <FormattedMessage id="user.security.old_password" />
              </Label>
              <Input
                type="password"
                name="old_password"
                id="old_password"
                placeholder={intl.formatMessage({ id: 'user.security.old_password_placeholder' })}
                value={securityFormik.values.old_password}
                onChange={securityFormik.handleChange}
                onBlur={securityFormik.handleBlur}
                invalid={securityFormik.touched.old_password && securityFormik.errors.old_password}
              />
              {securityFormik.touched.old_password && securityFormik.errors.old_password ? (
                <Badge color="danger" className="font-small-2" style={{ marginTop: '4px', display: 'block', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  {securityFormik.errors.old_password}
                </Badge>
              ) : null}
            </FormGroup>

            <FormGroup>
              <Label for="password">
                <FormattedMessage id="user.security.new_password" />
              </Label>
              <Input
                type="password"
                name="password"
                id="password"
                placeholder={intl.formatMessage({ id: 'user.security.new_password_placeholder' })}
                value={securityFormik.values.password}
                onChange={securityFormik.handleChange}
                onBlur={securityFormik.handleBlur}
                invalid={securityFormik.touched.password && securityFormik.errors.password}
              />
              {securityFormik.touched.password && securityFormik.errors.password ? (
                <Badge color="danger" className="font-small-2" style={{ marginTop: '4px', display: 'block', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  {securityFormik.errors.password}
                </Badge>
              ) : null}
            </FormGroup>
            <FormGroup>
              <Label for="password_confirmation">
                <FormattedMessage id="user.security.confirm_password" />
              </Label>
              <Input
                type="password"
                name="password_confirmation"
                id="password_confirmation"
                placeholder={intl.formatMessage({ id: 'user.security.confirm_password_placeholder' })}
                value={securityFormik.values.password_confirmation}
                onChange={securityFormik.handleChange}
                onBlur={securityFormik.handleBlur}
                invalid={securityFormik.touched.password_confirmation && securityFormik.errors.password_confirmation}
              />
              {securityFormik.touched.password_confirmation && securityFormik.errors.password_confirmation ? (
                <Badge color="danger" className="font-small-2" style={{ marginTop: '4px', display: 'block', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  {securityFormik.errors.password_confirmation}
                </Badge>
              ) : null}
            </FormGroup>
            <ModalFooter>
              <Button color="secondary" onClick={toggleSecurityModal}>
                <FormattedMessage id="button.cancel" />
              </Button>{' '}
              <Button color="primary" type="submit" onClick={securityFormik.handleSubmit}>
                <FormattedMessage id="button.save" />
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>

      </Modal>

      <Modal isOpen={isIuliModalOpen} toggle={toggleIuliModal} className="modal-dialog-centered">
        <ModalHeader toggle={toggleIuliModal}>Assinatura Iuli</ModalHeader>
        <ModalBody>
          <IuliPlanInfo
            iuliPlan={currentCompany.iuliPlan}
            dateExpiryAt={currentCompany.date_expiry_at}
            companyId={currentCompany.id}
          />
        </ModalBody>
      </Modal>
    </ul>
  );
};

NavbarUser.propTypes = {
  userData: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    mobile_phone: PropTypes.string,
    ddi: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,

  userImg: PropTypes.string,
  logout: PropTypes.func,
  currentCompany: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const loggedInUser = state.auth.login?.values?.loggedInUser;
  const isIuliAdmin = loggedInUser?.is_iuli_admin === true || loggedInUser?.is_iuli_admin === 1;
  
  return {
    currentCompany: state.companies.currentCompany,
    userData: loggedInUser,
    isIuliAdmin: Boolean(isIuliAdmin),
    currentPermissions: state?.companies?.currentCompany?.iuliPlan?.permissions,
    userPermissions: state.permissions.currentPermissions,
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

export const updateUser = async ({ mobile_phone_ddi, email, mobile_phone, password, old_password, avatar, apiOptions = {} } = {}) => {
  if (avatar) {
    const formData = new FormData();
    if (mobile_phone_ddi !== undefined) formData.append('mobile_phone_ddi', mobile_phone_ddi);
    if (email !== undefined) formData.append('email', email);
    if (mobile_phone !== undefined) formData.append('mobile_phone', mobile_phone);
    if (password !== undefined) formData.append('password', password);
    if (old_password !== undefined) formData.append('old_password', old_password);
    formData.append('_method', 'PUT');
    formData.append('avatar', avatar);

    return API(apiOptions)({
      url: `/user`,
      method: 'POST',
      data: formData,
    });
  }

  return API(apiOptions)({
    url: `/user`,
    method: 'PUT',
    data: { mobile_phone_ddi, email, mobile_phone, password, old_password },
  });
};

export default connect(mapStateToProps, mapDispatchToProps)(NavbarUser);